import {
  CheckCircle2,
  Download,
  Edit,
  FileUp,
  Loader2,
  Plus,
  X,
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { api, invoiceApi, InvoiceBatchLink, InvoiceCreatePayload, InvoiceDocumentPayload, InvoiceRecord } from '../api';
import { signUploadKeys } from '../api';
import { useAuth } from '../hooks/useAuth';
import ModalPortal from './ModalPortal';

const MAX_FILE_BYTES = 5 * 1024 * 1024;

type DraftInvoice = {
  invoice_date: string;
  invoice_amount: string;
  invoice_for: string;
  batch_ids: number[];
  document: InvoiceDocumentPayload | null;
};

const emptyDraft: DraftInvoice = {
  invoice_date: '',
  invoice_amount: '',
  invoice_for: '',
  batch_ids: [],
  document: null,
};

const allowedRolesForAdminView = ['SYSTEM_ADMIN', 'SUPER_ADMIN', 'OPERATIONS_ADMIN'] as const;

const InvoicesManagement: React.FC = () => {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState<InvoiceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    search: '',
    isPaid: '',
    mpId: '',
    batch: '',
  });
  const [pagination, setPagination] = useState({ total: 0, page: 1, pageSize: 20 });
  const [batchOptions, setBatchOptions] = useState<InvoiceBatchLink[]>([]);
  const [partners, setPartners] = useState<Array<{ mp_id: number; name: string }>>([]);
  const [showForm, setShowForm] = useState(false);
  const [draft, setDraft] = useState<DraftInvoice>(emptyDraft);
  const [editingInvoice, setEditingInvoice] = useState<InvoiceRecord | null>(null);
  const [existingDocument, setExistingDocument] = useState<InvoiceDocumentPayload | null>(null);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState<number | null>(null);

  const isMpAdmin = user?.role === 'MOBILISATION_PARTNER_ADMIN';
  const isSysAdmin = allowedRolesForAdminView.includes(user?.role as any);

  const currentPage = pagination.page;

  useEffect(() => {
    if (user) {
      setPagination((prev) => ({ ...prev, page: 1 }));
      fetchInvoices(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, filters.isPaid, filters.search, filters.mpId, filters.batch]);

  useEffect(() => {
    if (isMpAdmin) {
      loadBatchOptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMpAdmin]);

  useEffect(() => {
    if (isSysAdmin) {
      loadPartners();
    }
  }, [isSysAdmin]);

  const fetchInvoices = async (page: number) => {
    if (!user) return;
    try {
      setLoading(true);
      setError(null);
      const params: any = {
        page,
        pageSize: pagination.pageSize,
      };
      if (filters.search) params.search = filters.search;
      if (filters.isPaid === 'true') params.isPaid = true;
      if (filters.isPaid === 'false') params.isPaid = false;
      if (filters.batch) params.batch = filters.batch;
      if (isSysAdmin && filters.mpId) {
        params.mpId = Number(filters.mpId);
      }

      const response = await invoiceApi.list(params);
      setInvoices(response.data);
      setPagination(response.pagination);
    } catch (err: any) {
      console.error('Failed to fetch invoices', err);
      setError(err?.response?.data?.message || 'Failed to load invoices.');
    } finally {
      setLoading(false);
    }
  };

  const loadBatchOptions = async () => {
    try {
      const resp = await invoiceApi.listBatchOptions();
      if (resp.success) setBatchOptions(resp.data);
    } catch (err) {
      console.error('Failed to load batch list', err);
    }
  };

  const loadPartners = async () => {
    try {
      const resp = await api.get('/partners?status=');
      if (resp.data?.success) {
        setPartners(resp.data.data ?? []);
      }
    } catch (err) {
      console.error('Failed to load partners', err);
    }
  };

  const resetForm = () => {
    setDraft(emptyDraft);
    setExistingDocument(null);
    setEditingInvoice(null);
    setShowForm(false);
    setUploading(false);
    setFormError(null);
  };

  const openCreate = () => {
    setDraft(emptyDraft);
    setExistingDocument(null);
    setEditingInvoice(null);
    setShowForm(true);
    setSuccess(null);
    setError(null);
    setFormError(null);
  };

  const openEdit = (invoice: InvoiceRecord) => {
    setEditingInvoice(invoice);
    const missing = invoice.batches.filter(
      (b) => !batchOptions.some((opt) => opt.batch_id === b.batch_id)
    );
    if (missing.length) {
      setBatchOptions((prev) => [
        ...prev,
        ...missing.map((b) => ({ batch_id: b.batch_id, batch_code: b.batch_code ?? null })),
      ]);
    }
    setDraft({
      invoice_date: formatDateForInput(invoice.invoice_date),
      invoice_amount: invoice.invoice_amount ?? '',
      invoice_for: invoice.invoice_for ?? '',
      batch_ids: invoice.batches.map((b) => b.batch_id),
      document: null,
    });
    setExistingDocument({
      key: invoice.document_key,
      name: invoice.document_name ?? invoice.document_key,
      mime: invoice.document_mime ?? undefined,
      size: invoice.document_size ?? undefined,
    });
    setShowForm(true);
    setSuccess(null);
    setError(null);
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormError(null);
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_FILE_BYTES) {
      setFormError('File is larger than 5 MB.');
      return;
    }
    const mime = file.type || '';
    if (!(mime === 'application/pdf' || mime.startsWith('image/'))) {
      setFormError('Only PDF or image files are supported.');
      return;
    }

    const body = new FormData();
    body.append('file', file);
    setUploading(true);

    try {
      const response = await api.post('/uploads?folder=invoices', body, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const { key, filename, contentType } = response.data;
      const document: InvoiceDocumentPayload = {
        key,
        name: filename || file.name,
        mime: contentType || file.type,
        size: file.size,
      };
      setDraft((prev) => ({ ...prev, document }));
      setExistingDocument(document);
      setError(null);
      setFormError(null);
    } catch (err: any) {
      console.error('Upload failed', err);
      setFormError(err?.response?.data?.message || 'Unable to upload invoice document.');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!user) return;
    setSaving(true);
    setError(null);
    setFormError(null);
    setSuccess(null);

    try {
      const payloadBase = {
        invoice_date: draft.invoice_date,
        invoice_amount: draft.invoice_amount || '0',
        invoice_for: draft.invoice_for?.trim(),
        batch_ids: draft.batch_ids,
      };

      if (!editingInvoice) {
        if (!draft.document) {
          setFormError('Please upload the invoice document.');
          setSaving(false);
          return;
        }
        const payload: InvoiceCreatePayload = {
          ...payloadBase,
          document: draft.document,
        };
        await invoiceApi.create(payload);
        setSuccess('Invoice created successfully.');
      } else {
        const payload = {
          ...payloadBase,
          document: draft.document ?? undefined,
        };
        await invoiceApi.update(editingInvoice.invoice_id, payload);
        setSuccess('Invoice updated successfully.');
      }

      resetForm();
      fetchInvoices(currentPage);
      if (isMpAdmin) loadBatchOptions();
    } catch (err: any) {
      console.error('Failed to save invoice', err);
      setFormError(err?.response?.data?.message || 'Failed to save invoice.');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkPaid = async (invoice: InvoiceRecord) => {
    try {
      setSaving(true);
      await invoiceApi.markPaid(invoice.invoice_id);
      setSuccess(`Invoice ${invoice.invoice_number} marked as paid.`);
      fetchInvoices(currentPage);
    } catch (err: any) {
      console.error('Failed to mark invoice as paid', err);
      setError(err?.response?.data?.message || 'Failed to mark invoice as paid.');
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async (invoice: InvoiceRecord) => {
    setDownloading(invoice.invoice_id);
    try {
      const urls = await signUploadKeys([invoice.document_key]);
      const url = urls[invoice.document_key];
      if (url) {
        const link = document.createElement('a');
        link.href = url;
        link.download = invoice.document_name || invoice.document_key || 'invoice.pdf';
        link.rel = 'noopener';
        link.target = '_self';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        setError('Download link could not be generated.');
      }
    } catch (err) {
      console.error('Failed to download invoice', err);
      setError('Failed to fetch signed download link.');
    } finally {
      setDownloading(null);
    }
  };

  const totalPages = useMemo(() => {
    if (!pagination.pageSize) return 1;
    return Math.max(1, Math.ceil(pagination.total / pagination.pageSize));
  }, [pagination.total, pagination.pageSize]);

  const handlePageChange = (next: number) => {
    const nextPage = Math.min(Math.max(next, 1), totalPages);
    setPagination((prev) => ({ ...prev, page: nextPage }));
    fetchInvoices(nextPage);
  };

  const toggleBatchSelection = (batchId: number) => {
    setDraft((prev) => {
      const exists = prev.batch_ids.includes(batchId);
      return {
        ...prev,
        batch_ids: exists ? prev.batch_ids.filter((id) => id !== batchId) : prev.batch_ids.concat(batchId),
      };
    });
  };

  const selectedMpName = useMemo(() => {
    if (!filters.mpId) return '';
    const mpId = Number(filters.mpId);
    return partners.find((p) => p.mp_id === mpId)?.name ?? '';
  }, [filters.mpId, partners]);

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Invoices</h1>
          <p className="text-sm text-gray-600">
            Manage mobilisation partner invoices and track payment status.
          </p>
        </div>
        {isMpAdmin && (
          <button
            onClick={openCreate}
            className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            New Invoice
          </button>
        )}
      </div>

      {(error || success) && (
        <div
          className={`rounded-md border px-4 py-3 text-sm ${
            error ? 'border-red-200 bg-red-50 text-red-700' : 'border-green-200 bg-green-50 text-green-700'
          }`}
        >
          {error || success}
        </div>
      )}

      <div className="grid gap-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <div className="grid gap-4 md:grid-cols-4">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-600">Search</label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              placeholder="Invoice number or notes"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-600">Status</label>
            <select
              value={filters.isPaid}
              onChange={(e) => setFilters((prev) => ({ ...prev, isPaid: e.target.value }))}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
            >
              <option value="">All</option>
              <option value="false">Pending</option>
              <option value="true">Paid</option>
            </select>
          </div>
          {isSysAdmin && (
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-600">Mobilisation Partner</label>
              <select
                value={filters.mpId}
                onChange={(e) => setFilters((prev) => ({ ...prev, mpId: e.target.value }))}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              >
                <option value="">All</option>
                {partners.map((p) => (
                  <option key={p.mp_id} value={p.mp_id}>
                    {p.name ?? `MP #${p.mp_id}`}
                  </option>
                ))}
              </select>
              {selectedMpName && (
              <span className="text-xs text-gray-400">Selected: {selectedMpName}</span>
              )}
            </div>
          )}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-600">Batch Number</label>
            <input
              type="text"
              value={filters.batch}
              onChange={(e) => setFilters((prev) => ({ ...prev, batch: e.target.value }))}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              placeholder="Batch code or ID"
            />
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="px-4 py-3 text-left font-medium">Invoice #</th>
              {isSysAdmin && <th className="px-4 py-3 text-left font-medium">Partner</th>}
              <th className="px-4 py-3 text-left font-medium">Date</th>
              <th className="px-4 py-3 text-left font-medium">Amount</th>
              <th className="px-4 py-3 text-left font-medium">Batches</th>
              <th className="px-4 py-3 text-left font-medium">Invoice For</th>
              <th className="px-4 py-3 text-left font-medium">Status</th>
              <th className="px-4 py-3 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td colSpan={isSysAdmin ? 8 : 7} className="px-4 py-10 text-center text-gray-500">
                  <div className="inline-flex items-center gap-2">
                    <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
                    Loading invoices...
                  </div>
                </td>
              </tr>
            ) : invoices.length === 0 ? (
              <tr>
                <td colSpan={isSysAdmin ? 8 : 7} className="px-4 py-10 text-center text-gray-500">
                  No invoices found for the selected filters.
                </td>
              </tr>
            ) : (
              invoices.map((invoice) => (
                <tr key={invoice.invoice_id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{invoice.invoice_number}</td>
                  {isSysAdmin && (
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {invoice.mobilisation_partner?.name ?? `MP #${invoice.mp_id}`}
                    </td>
                  )}
                  <td className="px-4 py-3 text-sm text-gray-700">{formatDateDisplay(invoice.invoice_date)}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">₹{invoice.invoice_amount ?? '0'}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {invoice.batches.length ? invoice.batches.map((b) => b.batch_code || b.batch_id).join(', ') : '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{invoice.invoice_for || '—'}</td>
                  <td className="px-4 py-3 text-sm">
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                        invoice.is_paid
                          ? 'bg-green-100 text-green-700'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}
                    >
                      {invoice.is_paid ? 'Paid' : 'Pending'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right text-sm">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleDownload(invoice)}
                        className="inline-flex items-center rounded-md border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                      >
                        {downloading === invoice.invoice_id ? (
                          <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Download className="mr-1 h-3.5 w-3.5" />
                        )}
                        Document
                      </button>
                      {isMpAdmin && !invoice.is_paid && (
                        <button
                          onClick={() => openEdit(invoice)}
                          className="inline-flex items-center rounded-md border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                        >
                          <Edit className="mr-1 h-3.5 w-3.5" />
                          Edit
                        </button>
                      )}
                      {isSysAdmin && !invoice.is_paid && (
                        <button
                          onClick={() => handleMarkPaid(invoice)}
                          disabled={saving}
                          className="inline-flex items-center rounded-md bg-green-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-green-400"
                        >
                          <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
                          Mark Paid
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm">
          <span className="text-gray-600">
            Page {pagination.page} of {totalPages} · {pagination.total} total
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="rounded-md border border-gray-300 px-3 py-1 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400"
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === totalPages}
              className="rounded-md border border-gray-300 px-3 py-1 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {showForm && (
        <ModalPortal>
          <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/50 px-4">
            <div className="relative w-full max-w-3xl rounded-lg bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {editingInvoice ? `Edit Invoice ${editingInvoice.invoice_number}` : 'Create Invoice'}
                </h2>
                <p className="text-xs text-gray-500">
                  Fill the invoice details and upload the invoice document (PDF or image, ≤ 5 MB).
                </p>
              </div>
              <button onClick={resetForm} className="rounded-full p-1 text-gray-500 hover:bg-gray-100">
                <X className="h-5 w-5" />
              </button>
            </div>
            {formError && (
              <div className="mx-6 mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {formError}
              </div>
            )}
            <form
              onSubmit={handleSubmit}
              onChangeCapture={() => {
                setFormError(null);
                setError(null);
              }}
              className="space-y-5 px-6 py-6"
            >
              <div className="grid gap-4 md:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-gray-600">Invoice Date</label>
                  <input
                    type="date"
                    required
                    value={draft.invoice_date}
                    onChange={(e) => setDraft((prev) => ({ ...prev, invoice_date: e.target.value }))}
                    className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-gray-600">Invoice Amount</label>
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    required
                    value={draft.invoice_amount}
                    onChange={(e) => setDraft((prev) => ({ ...prev, invoice_amount: e.target.value }))}
                    className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-gray-600">Invoice For</label>
                <input
                  type="text"
                  value={draft.invoice_for}
                  onChange={(e) => setDraft((prev) => ({ ...prev, invoice_for: e.target.value }))}
                  className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  placeholder="e.g. First Trench"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-gray-600">Batches</label>
                <div className="grid max-h-48 gap-2 overflow-y-auto rounded-md border border-gray-200 p-3">
                  {batchOptions.length === 0 ? (
                    <span className="text-sm text-gray-500">No batches available.</span>
                  ) : (
                    batchOptions.map((batch) => {
                      const selected = draft.batch_ids.includes(batch.batch_id);
                      return (
                        <label key={batch.batch_id} className="flex items-center gap-2 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => toggleBatchSelection(batch.batch_id)}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span>{batch.batch_code || `Batch #${batch.batch_id}`}</span>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-gray-600">Invoice Document</label>
                <div className="rounded-md border border-dashed border-gray-300 p-4 text-sm text-gray-600">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="font-medium text-gray-800">
                        {existingDocument?.name ?? 'No file uploaded yet'}
                      </p>
                      {existingDocument?.mime && (
                        <p className="text-xs text-gray-500">
                          {existingDocument.mime} · {formatFileSize(existingDocument.size)}
                        </p>
                      )}
                    </div>
                    <label className="inline-flex cursor-pointer items-center gap-2 rounded-md bg-blue-600 px-3 py-2 text-xs font-medium text-white hover:bg-blue-700">
                      <FileUp className="h-4 w-4" />
                      {uploading ? 'Uploading...' : 'Upload'}
                      <input type="file" accept="application/pdf,image/*" className="hidden" onChange={handleFileSelect} />
                    </label>
                  </div>
                  <p className="mt-2 text-xs text-gray-500">Accepted formats: PDF or image files up to 5 MB.</p>
                </div>
              </div>
              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={resetForm}
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving || uploading}
                  className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-400"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                  {editingInvoice ? 'Update Invoice' : 'Create Invoice'}
                </button>
              </div>
            </form>
            </div>
          </div>
        </ModalPortal>
      )}
    </div>
  );
};

export default InvoicesManagement;

function formatDateForInput(value: string | Date | null | undefined) {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

function formatDateDisplay(value: string | Date | null | undefined) {
  if (!value) return '—';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString();
}

function formatFileSize(size?: number | null) {
  if (!size || size <= 0) return '';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
}
